// src/components/MobileMenu.tsx

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import type { MobileMenuLink, NavItem } from '../Header.types';
import { Button } from '@/design-system/Atoms';
import { usePatientCTAButtonConfig } from '@/hooks/usePatientCTAButtonConfig';

interface MobileMenuProps {
    isOpen: boolean;
    navItems: NavItem[];
    ctaButton: {
        text: string;
        to: string;
        onClick?: () => void;
        className?: string;
    };
    mobileMenuLinks?: MobileMenuLink[];
    onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
    isOpen,
    navItems,
    ctaButton,
    mobileMenuLinks = [],
    onClose,
}) => {
    const [height, setHeight] = useState(0);
    const { ctaButtonConfig, patientEntitlementsIsLoading } =
      usePatientCTAButtonConfig("Get Started");

    useEffect(() => {
      const fullHeight = window.innerHeight;

      if (isOpen) {
        setHeight(0);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setHeight(fullHeight);
          });
        });
      } else {
        setHeight(fullHeight);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setHeight(0);
          });
        });
      }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    return (
        <div
            className="hide-above-991 overflow-hidden transition-[height] duration-[350ms] ease-in-out"
            style={{ height: `${height}px` }}
        >
            <Navigation items={navItems} />

            <Button
                variant="secondary"
                size="full"
                fullWidth
                className={`btn ${ctaButton.className || 'btn-secondary mb-5'}`}
                onClick={ctaButtonConfig.action}
                loading={patientEntitlementsIsLoading}
            >
                {ctaButtonConfig.text}
            </Button>

            {mobileMenuLinks.length > 0 && (
                <ul>
                    {mobileMenuLinks.map((link) => (
                        <li key={link.id} className={link.className}>
                            <Link
                                to={link.to}
                                className={`underline-animate ${link.id === 'privacy' || link.id === 'terms'
                                    ? 'text-[1rem]'
                                    : 'text-[1.25rem]'
                                    } mb-2 text-white`}
                                onClick={onClose}
                            >
                                {link.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default MobileMenu;
